<!-- biome-ignore-all lint/correctness/useVueVForKey: Init Biome -->

<script setup lang="ts">
import { computed, useSlots } from "vue";

import Checkbox from "@/dsComponents/checkbox/Checkbox.vue";
import { DropdownItemColors, type DropdownItemProps } from "@/dsComponents/Dropdown/types";

import Avatar, { AvatarSize } from "../Avatar";
import Loader from "../Loader.vue";

const props = withDefaults(defineProps<DropdownItemProps>(), {
    fontSize: "12",
    fontWeight: "400",
    labelColor: DropdownItemColors.lightGrey,
    textElipsis: true,
    overflows: false,
});

defineExpose({
    checked: props.checked,
});

const emit = defineEmits(["click", "select"]);
const slots = useSlots();
const hasSlot = computed(() => !!slots.default);

const highlightLabels = computed<{ label: string; highlighted: boolean; spaceEnd?: boolean }[]>(() => {
    const stringifiedLabel = String(props.label);
    if (props.highlight) {
        // Build regex to highlight parts according with highlight props.
        const highlightRegex = new RegExp(
            props.highlight
                .trim()
                .split(/\s+/)
                .filter(Boolean)
                .sort((a, b) => b.length - a.length)
                .join("|"),
            "ig"
        );
        const isHighlighted = highlightRegex.test(stringifiedLabel);
        if (!isHighlighted) return [{ label: stringifiedLabel, highlighted: false }];

        const labelSpaceParts = stringifiedLabel.split(/\s+/).filter(Boolean);

        return labelSpaceParts.flatMap((label) => {
            const labelHighligthParts = label.split(highlightRegex);
            // Get corresponding hightlight parts to keep the case sensitivity.
            const highlightParts = label.match(highlightRegex);
            const final = [];
            // Loop over NOT highlighted (NH) parts and add the highligh part between each NH parts in final.
            for (let i = 0; i < labelHighligthParts.length; i++) {
                const part = labelHighligthParts[i];
                const spaceEnd = i === labelHighligthParts.length - 1;
                final.push({ label: part.trim(), highlighted: false, spaceEnd });
                if (i < labelHighligthParts.length - 1) {
                    // Add the corresponding highlight part.
                    // biome-ignore lint/style/noNonNullAssertion: Init Biome
                    final.push({ label: highlightParts![i].trim(), highlighted: true });
                }
            }
            return final;
        });
    }
    return [{ label: stringifiedLabel, highlighted: false }];
});

const handleClick = (event: MouseEvent) => {
    if (props.disabled) return;
    if (props.stop) {
        event.stopPropagation();
    }
    emit(props.checkbox ? "select" : "click");
};
</script>
<template>
    <button
        :class="['ds-dropdown-item', { loading, checked, disabled, overflows, 'no-hover': noHover }, backGroundColor]"
        @click="handleClick"
        :style="{ cursor: cursor ?? '', ...customStyles }"
    >
        <div class="left">
            <Loader v-if="loading" />
            <Checkbox v-if="checkbox" size="sm" :checked="checked ?? false" :disabled="disabled" @input="handleClick" />
            <i v-if="iconLeft && !loading" :class="['ti', 'icon-left', `ti-${iconLeft}`, iconLeftColor]"></i>
            <Avatar v-if="avatar" v-bind="avatar" :size="AvatarSize.size20" />
            <div v-if="label || hasSlot" class="item-label">
                <span :class="['label-container', `font-size-${fontSize}`, { flex, highlight }]" :style="{ fontWeight }">
                    <span
                        v-for="{ label, highlighted, spaceEnd } of highlightLabels"
                        :class="[labelColor, { highlighted, spaceEnd }, { 'text-elipsis': textElipsis }]"
                    >
                        <slot>
                            {{ label }}
                        </slot>
                    </span>
                </span>
            </div>
        </div>
        <div v-if="hasSubmenu" class="right submenu-indicator">
            <i class="ti ti-chevron-right"></i>
        </div>
        <div v-else-if="contentRight" class="right">
            <div v-if="contentRight.icon" :class="['content-right-icon', contentRight.iconStyle]">
                <i :class="['ti', `ti-${contentRight.icon}`, contentRight.iconColor ?? DropdownItemColors.white]"></i>
            </div>
            <span :class="['content-right-text', contentRight.style]" v-if="contentRight.text">{{ contentRight.text }}</span>
        </div>
    </button>
</template>
<style scoped lang="scss">
@mixin dropdown-item-colors {
    .white {
        color: $white;
    }

    .blue {
        color: $blue-base;
    }

    .purple {
        color: $purple-base;
    }

    .red {
        color: $red-base;
    }

    .orange {
        color: $orange-base;
    }

    .yellow {
        color: $yellow-base;
    }

    .green {
        color: $green-base;
    }

    .grey {
        color: $icon-sub-500;
    }

    .black {
        color: $text-main-900;
    }
}

.ds-dropdown-item {
    display: flex;
    padding: 8px;
    justify-content: space-between;
    align-items: center;
    border-radius: 8px;
    transition: 0.2s;
    white-space: nowrap;
    cursor: pointer;
    background-color: white;
    width: 100%;
    color: $text-main-900;
    gap: 28px;
    height: min-content;

    &.loading {
        opacity: 50%;
        &:hover:not(.no-hover) {
            cursor: not-allowed;
        }
        &:active {
            pointer-events: none;
        }
    }
    &.checked {
        background-color: $bg-weak-100;
    }

    &:hover:not(.no-hover, .disabled),
    &.active {
        background-color: $bg-weak-100;
    }
    &.disabled {
        pointer-events: none;
    }
    &.overflows {
        overflow: visible;
        & > * {
            overflow: visible !important;
        }
    }
    &.no-hover {
        cursor: default;
    }
    .left {
        display: flex;
        gap: 8px;
        align-items: center;
        transition: 0.2s;
        overflow: hidden;
        pointer-events: none;

        & > *:not(.ds-checkbox, .ti, .ds-loader) {
            flex: 1;
        }
        .item-label {
            display: flex;
            gap: 0;
            width: 100%;
            overflow: hidden;
            pointer-events: none;
            .spaceEnd {
                margin-right: 4px;
            }

            .label-container {
                width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                text-align: left;
                white-space: pre-wrap;

                &.font-size-12 {
                    span {
                        @include font-size-12;
                    }
                }
                &.font-size-13 {
                    span {
                        @include font-size-13;
                    }
                }

                &.flex,
                &.highlight {
                    display: flex;
                }

                .highlighted {
                    color: $blue-base;
                }

                .text-elipsis {
                    white-space: nowrap;
                }
            }
        }
        .icon-left {
            font-size: 16px;
        }
    }
    .right {
        display: flex;
        align-items: center;
        gap: 4px;
        pointer-events: none;
        .content-right-text.quiet {
            @include font-size-10;
            color: $text-soft-400;
            font-weight: 400;
        }
        .content-right-icon {
            &.rounded {
                border-radius: var(--raidus-xl, 12px);
                background-color: var(--blue-base, #2051f8);
                width: 14px;
                height: 14px;
                display: flex;
                align-items: center;
                justify-content: center;
                i {
                    font-size: 10px;
                    color: var(--icon-white-0, #ffffff) !important;
                }
            }
            i {
                font-size: 16px;
            }
        }
        &.submenu-indicator {
            i {
                font-size: 12px;
                color: var(--icon-sub-500, #636778);
            }
        }
    }

    .lightGrey {
        color: $text-sub-600;
    }

    @include dropdown-item-colors;

    &.disabled {
        user-select: none;
        cursor: not-allowed;
        span,
        i {
            color: $text-disabled-300;
        }
    }

    .black {
        color: $text-main-900;
    }

    &.red {
        &:hover:not(.no-hover, .disabled),
        &.active {
            background-color: var(--red-lighter, #fdedf0);
        }
    }

    &.blue {
        &:hover:not(.no-hover, .disabled),
        &.active {
            background-color: var(--blue-lighter, #ebefff);
        }
    }

    &.purple {
        &:hover:not(.no-hover, .disabled),
        &.active {
            background-color: var(--purple-lighter, #f4edfd);
        }
    }

    &.orange {
        &:hover:not(.no-hover, .disabled),
        &.active {
            background-color: var(--orange-lighter, #fff1eb);
        }
    }

    &.yellow {
        &:hover:not(.no-hover, .disabled),
        &.active {
            background-color: var(--yellow-lighter, #fef7e6);
        }
    }

    &.green {
        &:hover:not(.no-hover, .disabled),
        &.active {
            background-color: var(--green-lighter, #ecfef5);
        }
    }
}
</style>
